import React from 'react';
import { ChatMessage, ModalType, Sender } from '../types';
import { CosmosLogoIcon } from './icons';
import { SamplePromptCard } from './SamplePromptCard';
import { LandingChatMessage } from './LandingChatMessage';
import { ChatInput } from './ChatInput';
import { FollowUpQuestions } from './FollowUpQuestions';
import { ThinkingAnimation } from './ThinkingAnimation';
import { StreamStatus } from '../services/apiService';
  
interface LandingChatInterfaceProps {
    messages: ChatMessage[];
    onSend: (prompt: string, files: File[]) => void;
    isLoading: boolean;
    openModal: (type: ModalType) => void;
    samplePrompts: { title: string; prompt: string; }[];
    welcomeMessage?: string;
    showConciergeLink?: boolean;
    onResetChat?: () => void;
    showResetButton?: boolean;
    onReadMore: () => void;
    // Streaming status props
    streamingStatus?: StreamStatus | null;
    streamingMessage?: string | null;
    streamingResultsCount?: number;
}

const defaultWelcomeMessage = "Hi! I'm Cosmos Mini — a preview to help you scout high-potential university innovations. I can help you with";

export const LandingChatInterface: React.FC<LandingChatInterfaceProps> = ({ 
    messages, 
    onSend, 
    isLoading, 
    openModal, 
    samplePrompts, 
    welcomeMessage = defaultWelcomeMessage, 
    showConciergeLink,
    onResetChat,
    showResetButton = false,
    onReadMore,
    streamingStatus = null,
    streamingMessage = null,
    streamingResultsCount = 0
}) => {
  return (
    <main className="flex flex-col flex-1 self-stretch rounded-2xl bg-grayscale-100 shadow-custom-light px-2 sm:px-4 md:px-6 lg:px-12 xl:px-16 pt-4 overflow-x-hidden">
      <div className="flex-1 flex flex-col justify-between overflow-y-auto hide-scrollbar min-w-0">
        <div className="flex-grow">
          {messages.length === 0 && !isLoading ? (
            <div className="flex flex-col items-start gap-3 sm:gap-4 w-full min-w-0">
              <div className="flex items-start gap-2 self-stretch min-w-0">
                <div className="flex-shrink-0">
                  <CosmosLogoIcon />
                </div>
                <p className="text-grayscale-1200 font-grotesk text-xs sm:text-sm min-w-0 flex-1">
                  {welcomeMessage}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 self-stretch w-full">
                {samplePrompts.map((p, i) => (
                  <SamplePromptCard key={i} title={p.title} prompt={p.prompt} onClick={(prompt) => onSend(prompt, [])} />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4 w-full min-w-0">
              {messages.map(msg => (
                <LandingChatMessage 
                  key={msg.id} 
                  message={msg}
                  onReadMore={onReadMore}
                />
              ))}
              {isLoading && (
                 <div className="flex items-start gap-2 sm:gap-4 p-2 sm:p-4 rounded-xl w-full min-w-0">
                    <div className="flex-shrink-0">
                      <CosmosLogoIcon />
                    </div>
                    <div className="flex-1 min-w-0">
                      <ThinkingAnimation 
                        isExtensiveSearch={true}
                        streamingStatus={streamingStatus}
                        streamingMessage={streamingMessage}
                        resultsCount={streamingResultsCount}
                      />
                    </div>
                  </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-between items-center my-2 sm:my-4 w-full min-w-0">
            {/* <p className="text-grayscale-1300 font-grotesk text-xs">Cosmos features cutting-edge technology and research from 10 leading universities across the USA</p> */}
            {showResetButton && onResetChat && messages.length > 0 && (
                <button 
                    onClick={onResetChat}
                    className="text-primary-og-600 font-grotesk text-[10px] sm:text-xs font-bold underline hover:text-primary-700 transition-colors flex-shrink-0"
                >
                    Reset Chat
                </button>
            )}
        </div>
      </div>

      {/* Follow-up Questions - TEMPORARILY DISABLED */}
      {/* {messages.length > 0 && !isLoading && (
        <div className="px-4 pb-4">
          <FollowUpQuestions
            chatHistory={messages.map(msg => ({
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.message
            }))}
            onQuestionClick={(question) => onSend(question, [])}
          />
        </div>
      )} */}

      <ChatInput onSend={onSend} isLoading={isLoading} openModal={openModal} showConciergeLink={showConciergeLink} hasMessages={messages.length > 0} disableStickyFocus={true} disableAttachments={true} />
    </main>
  );
};
